import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, switchMap, of, throwError, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { ConfigService } from '../../shared/services/config.service';
import {
    LoginRequestDto,
    RefreshTokenRequestDto,
    TokenResponseDto,
    ClaimsResponse,
    PasswordResetResponseDto,
    ChangePasswordRequestDto,
} from '../../shared/models/auth.models';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

@Injectable({
    providedIn: 'root',
})
export class AuthService {

    private _http: HttpClient = inject(HttpClient);
    private _router: Router = inject(Router);
    private _config: ConfigService = inject(ConfigService);
    private _dialogService: DialogService = inject(DialogService);

    private _dialogRef: DynamicDialogRef | undefined = undefined;

    /**
     * Raised for the duration of an in-progress logout (user-initiated or
     * interceptor force-logout). Route guards check this to route to
     * /session-expired instead of /access-denied during the brief window
     * between local state being cleared and the navigation completing.
     * The interceptor checks this to avoid converting a stray 401 from a
     * request still in flight into a second, racing force-logout.
     * Owned by logout(); no external setter.
     */
    private _isLoggingOut: boolean = false;
    public get isLoggingOut(): boolean {
        return this._isLoggingOut;
    }

    /** Handle of the proactive refresh timer (cleared on logout / re-set on each token issuance). */
    private _refreshTimer: ReturnType<typeof setTimeout> | undefined = undefined;

    /**
     * Emits once at the start of every logout. The HTTP interceptor wraps each
     * non-auth request in takeUntil(logout$) so any in-flight request started
     * before the logout is cancelled the moment the user signs out — otherwise
     * the stale request would linger in the network panel and, if it 401s,
     * race against the logout's navigation.
     *
     * Auth endpoints (login, refresh, logout itself, etc.) are exempt from
     * this cancellation so the logout's own BE revoke call can complete.
     */
    private _logout$: Subject<void> = new Subject<void>();
    public readonly logout$: Observable<void> = this._logout$.asObservable();

    public getNewRefreshToken(): Observable<TokenResponseDto> {

        const userId: string | undefined = this.getClaims()?.userId;
        const refreshToken: string | null = localStorage.getItem('refreshToken');

        if (!userId || !refreshToken) {
            return throwError((): Error => new Error('no creds to refresh token'));
        }

        const request: RefreshTokenRequestDto = new RefreshTokenRequestDto({ userId: userId, refreshToken: refreshToken });

        return this._http.post<TokenResponseDto>(this._config.apiUrl + 'api/auth/refresh-token', request).pipe(
            tap((x: TokenResponseDto): void => {
                localStorage.setItem('accessToken', x.accessToken);
                localStorage.setItem('refreshToken', x.refreshToken);
                this.scheduleProactiveRefresh();
            })
        );

    }

    public hasActiveSession(): Observable<boolean> {
        let userId: string | undefined = this.getClaims()?.userId;
        let refreshToken: string | null = localStorage.getItem('refreshToken');
        
        if (!userId || !refreshToken){
            return of(false);
        } 

        const request: RefreshTokenRequestDto = new RefreshTokenRequestDto({ userId: userId, refreshToken: refreshToken });    

        return this._http.post<boolean>(
            this._config.apiUrl + 'api/auth/validate-user', request
        );

    }

    public login(email: string, password: string): Observable<TokenResponseDto> {

        const request: LoginRequestDto = new LoginRequestDto({ email: email, password: password });

        return this._http
            .post<TokenResponseDto>(this._config.apiUrl + 'api/auth/login', request)
            .pipe(
                switchMap((x: TokenResponseDto): Observable<TokenResponseDto> => {
                    if (x && x.accessToken && x.refreshToken) {
                        localStorage.setItem('accessToken', x.accessToken);
                        localStorage.setItem('refreshToken', x.refreshToken);
                        this._isLoggingOut = false;
                        this.scheduleProactiveRefresh();
                        return this.fetchClaims().pipe(switchMap((): Observable<TokenResponseDto> => of(x)));
                    }
                    return of(x);
                })
            );
    }

    public forgotPassword(email: string): Observable<unknown> {
        return this._http.post<unknown>(this._config.apiUrl + 'api/auth/forgot-password', { email: email });
    }

    public validatePasswordResetToken(token: string, email: string): Observable<unknown> {
        return this._http.post<unknown>(this._config.apiUrl + 'api/auth/validate-reset-token', { token: token, email: email });
    }

    public resetPassword(token: string, email: string, password: string): Observable<unknown> {
        return this._http.post<unknown>(this._config.apiUrl + 'api/auth/reset-password', { token: token, email: email, newPassword: password });
    }

    public changePassword(request: ChangePasswordRequestDto): Observable<PasswordResetResponseDto> {
        return this._http.post<PasswordResetResponseDto>(this._config.apiUrl + 'api/auth/change-password', request);
    }

    public fetchClaims(): Observable<ClaimsResponse> {
        return this._http
            .get<ClaimsResponse>(this._config.apiUrl + 'api/auth/claims')
            .pipe(
                tap((x: ClaimsResponse): void => {
                    localStorage.setItem('userClaims', JSON.stringify(x));              
                })
            );
    }

    public refreshClaims(): Observable<ClaimsResponse> {       
        return this._http
            .get<ClaimsResponse>(this._config.apiUrl + 'api/auth/refresh-claims')
            .pipe(
                tap((x: ClaimsResponse): void => {
                    localStorage.setItem('userClaims', JSON.stringify(x));           
                })
            );
    }

    public getClaims(): ClaimsResponse | undefined {
        let claims: ClaimsResponse = JSON.parse(localStorage.getItem('userClaims') || '{}');            
        
        if (!claims?.userId) {
            return undefined;
        }

        return claims;
    }

    /**
     * Perform a complete logout: revoke the current session on the server,
     * wipe local state, and navigate to the given destination (default
     * /auth/login — force-logout from the interceptor passes /session-expired).
     *
     * The isLoggingOut flag is raised BEFORE any state is cleared so that
     * any request still in flight that 401s during this window is treated
     * as collateral by the interceptor (no refresh attempt, no force-logout
     * navigation that would race against and override this one). The flag
     * is lowered only after the target navigation has completed.
     *
     * Idempotent: a re-entrant call during an in-progress logout is a no-op.
     */
    public logout(navigateTo: string = '/auth/login'): void {
        if (this._isLoggingOut) return;
        this._isLoggingOut = true;

        // Capture credentials BEFORE clearing storage so the BE revoke call
        // has something to look up. The HTTP call is fire-and-forget — local
        // state is the source of truth for the SPA, and a failed BE revoke
        // should not block the user from being logged out.
        const userId: string | undefined = this.getClaims()?.userId;
        const refreshToken: string | null = localStorage.getItem('refreshToken');

        this.cancelProactiveRefresh();

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userClaims');
        localStorage.removeItem('redirectUrl');
        localStorage.removeItem('userPermissions');
        // User-scoped UI filter state — clear so the next user doesn't inherit
        // the previous user's filter selection on the all-properties tab.
        localStorage.removeItem('allPropertiesFilter');

        // Fire the BE revoke BEFORE emitting the cancel signal so that the
        // interceptor dispatches this request before anything else gets torn
        // down. Auth endpoints (including /logout) are also exempt from the
        // takeUntil in the interceptor, so even if subscription order shifted,
        // this request would still complete.
        if (userId && refreshToken) {
            const request: RefreshTokenRequestDto = new RefreshTokenRequestDto({ userId, refreshToken });
            this._http.post(this._config.apiUrl + 'api/auth/logout', request).subscribe({
                next: (): void => {},
                error: (): void => {},
            });
        }

        // Cancel every other in-flight, non-auth HTTP subscription. Without
        // this, a request that was already over the wire when the user clicked
        // logout would still complete after navigation, linger in the network
        // panel and (if it 401s) have its error propagate to a component that
        // is about to be destroyed.
        this._logout$.next();

        this._router.navigateByUrl(navigateTo).finally((): void => {
            this._isLoggingOut = false;
        });
    }

    /**
     * Schedule a proactive token refresh based on the access token's `exp` claim.
     * Refresh is fired ~60 seconds before expiry; if the token is already expired
     * or near-expiry, the refresh runs immediately. Replaces any existing timer.
     */
    public scheduleProactiveRefresh(): void {
        this.cancelProactiveRefresh();

        const token: string | null = localStorage.getItem('accessToken');
        if (!token) return;

        const expiryMs: number | undefined = this._getTokenExpiryMs(token);
        if (expiryMs === undefined) return;

        // Refresh 60s before expiry; clamp to a minimum of 0 (run immediately if past or close).
        const refreshAt: number = expiryMs - Date.now() - 60_000;
        const delay: number = Math.max(0, refreshAt);

        this._refreshTimer = setTimeout((): void => {
            // If user logged out before the timer fired, do nothing.
            if (this._isLoggingOut || !localStorage.getItem('refreshToken')) return;
            this.getNewRefreshToken().subscribe({
                next: (): void => {/* scheduleProactiveRefresh re-runs from the tap in getNewRefreshToken */},
                error: (): void => {/* interceptor / next 401 will handle force-logout */},
            });
        }, delay);
    }

    public cancelProactiveRefresh(): void {
        if (this._refreshTimer !== undefined) {
            clearTimeout(this._refreshTimer);
            this._refreshTimer = undefined;
        }
    }

    /** Decode a JWT and return the `exp` claim as epoch-millis, or undefined if not parseable. */
    private _getTokenExpiryMs(token: string): number | undefined {
        try {
            const parts: string[] = token.split('.');
            if (parts.length < 2) return undefined;
            // base64url -> base64
            const base64: string = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded: string = base64 + '==='.slice((base64.length + 3) % 4);
            const json: string = atob(padded);
            const payload: { exp?: number } = JSON.parse(json);
            if (typeof payload.exp !== 'number') return undefined;
            return payload.exp * 1000;
        } catch {
            return undefined;
        }
    }

    public get isAdmin(): boolean {

        const impersonatedRole = localStorage.getItem('impersonatedUserRole') || '';
        if (impersonatedRole) return impersonatedRole === 'Admin';

        return this.getClaims()?.role === 'Admin';
    }

    public get isAuthenticated(): boolean {
        return !!this.getClaims();
    }

    public get userId(): string | undefined {

        const claims: ClaimsResponse | undefined = this.getClaims();
        if (!claims?.userId) return undefined;

        return claims?.userId;
    }

}
